/**
 * An account's permission chip (TMMAPP-0151 §D 11, §E 13; visual contract v2 §8): on an owned account
 * "Only you" (private tone) or "{name} can edit / can view / can import" for its first grantee, with
 * "+{n}" for more (shared tone), from `AccountResponse.grantees[]` (TMMAPI-0186 U3); on an account
 * granted to the caller, "{owner}'s" (lavender). Null where the grants aren't served (a single-account
 * read of an owned account), so the chip claims nothing.
 */
import type { TFunction } from 'i18next';
import type { Account, AccountGrantee } from '@/api/accounts';
import { personName } from '@/i18n/personName';
import type { PrivacyChipContent } from '@/shell/privacy';

/** The highest flag a grantee holds: edit implies view-full implies import (MM-D-11). */
function granteeText(grantee: AccountGrantee, t: TFunction): string {
  const name = personName(grantee.displayName, t);
  if (grantee.canEdit) return t('accounts.permChip.canEdit', { name });
  if (grantee.canViewFull) return t('accounts.permChip.canView', { name });
  return t('accounts.permChip.canImport', { name });
}

export function accountPermissionChip(
  account: Pick<Account, 'access' | 'ownerDisplayName' | 'grantees'>,
  t: TFunction,
): PrivacyChipContent | null {
  if (account.access !== 'OWNER') {
    return {
      tone: 'partner',
      label: t('accounts.permChip.owners', { owner: personName(account.ownerDisplayName, t) }),
    };
  }
  const grantees = account.grantees;
  if (grantees === null) return null;
  const first = grantees[0];
  if (first === undefined) return { tone: 'private', label: t('accounts.permChip.onlyYou') };
  const text = granteeText(first, t);
  return {
    tone: 'shared',
    label:
      grantees.length > 1
        ? t('accounts.permChip.more', { text, count: grantees.length - 1 })
        : text,
  };
}

/** A holding is the owner's alone (MM-D-43). */
export function holdingPermissionChip(t: TFunction): PrivacyChipContent {
  return { tone: 'private', label: t('accounts.permChip.onlyYou') };
}
